import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MDBBtn, MDBSpinner } from "mdb-react-ui-kit";
import PageWrapper from "../components/Common/PageWrapper";
import { useMessageBox } from "../components/Common/MessageBox";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/rolePermissions";
import { getUserId } from "../services/ordersApi";//
import {
  fetchOrderDashboardStats,
  fetchOrderDashboardClientWise,
  fetchOrderDashboardPlatformWise,
  fetchOrderDashboardVendorWise,
} from "../services/orderDashboardApi";
import "./OrderDashboardPage.css";

const MARKETPLACE_LABELS = {
  ebay: "eBay",
  vinted: "Vinted",
  shopify: "Shopify",
  poshmark: "Poshmark",
};

const MARKETPLACE_ORDER = ["ebay", "vinted", "shopify", "poshmark"];

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatMarketplace(value) {
  const key = String(value || "").toLowerCase();
  return MARKETPLACE_LABELS[key] || value || "—";
}

function getDefaultDateRange() {
  const today = new Date();
  const from = new Date(today.getFullYear(), today.getMonth(), 1);
  const toIso = (d) => d.toISOString().slice(0, 10);
  return { from: toIso(from), to: toIso(today) };
}

function StatCard({ label, value, variant = "" }) {
  return (
    <div className={`order-dashboard-stat-card ${variant}`}>
      <span className="order-dashboard-stat-label">{label}</span>
      <span className="order-dashboard-stat-value">{toNumber(value).toLocaleString()}</span>
    </div>
  );
}

function DashboardTable({ title, columns, rows, emptyMessage }) {
  return (
    <section className="order-dashboard-section">
      <h2 className="order-dashboard-section-title">{title}</h2>
      <div className="order-dashboard-table-wrap">
        {rows.length === 0 ? (
          <div className="order-dashboard-empty">{emptyMessage}</div>
        ) : (
          <table className="order-dashboard-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id ?? index}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

const OrderDashboardPage = () => {
  const { user, companyId } = useAuth();
  const { showError } = useMessageBox();
  const userRole = normalizeRole(user?.role);
  const userId = getUserId(user);

  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [appliedDateRange, setAppliedDateRange] = useState(getDefaultDateRange);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [clientWise, setClientWise] = useState([]);
  const [platformWise, setPlatformWise] = useState([]);
  const [vendorWise, setVendorWise] = useState([]);

  const applyDateFilter = () => {
    setAppliedDateRange({ ...dateRange });
  };


  const showErrorRef = useRef(showError);
  useEffect(() => { showErrorRef.current = showError; }, [showError]);

  const loadDashboard = useCallback(async () => {
    if (!companyId || !userId) return;

    setLoading(true);
    setError("");

    // fetchParams object ki jagah direct values use karo
    const params = {
      companyId,
      userId,
      userRole: user?.role || userRole,
      dateFrom: appliedDateRange.from || undefined,
      dateTo:   appliedDateRange.to   || undefined,
    };

    try {
      const [statsData, clientData, platformData, vendorData] = await Promise.all([
        fetchOrderDashboardStats(params),
        fetchOrderDashboardClientWise(params),
        fetchOrderDashboardPlatformWise(params),
        fetchOrderDashboardVendorWise(params),
      ]);

      setStats(statsData);
      setClientWise(Array.isArray(clientData)   ? clientData   : []);
      setPlatformWise(Array.isArray(platformData) ? platformData : []);
      setVendorWise(Array.isArray(vendorData)   ? vendorData   : []);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load order dashboard data.";
      setError(message);
      showErrorRef.current(message);
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    userId,
    user?.role,
    userRole,
    appliedDateRange.from,
    appliedDateRange.to,
  ]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const overall = stats?.overall ?? {};
  const marketplaceStats = useMemo(() => {
    const map = {};
    (stats?.by_marketplace || []).forEach((item) => {
      map[String(item.marketplace || "").toLowerCase()] = item;
    });
    return MARKETPLACE_ORDER.map((key) => ({
      marketplace: key,
      label: MARKETPLACE_LABELS[key],
      total_orders: map[key]?.total_orders ?? 0,
    }));
  }, [stats]);

  const clientColumns = [
    {
      key: "store_name",
      label: "Client Store",
      render: (row) => row.store_name || "—",
    },
    {
      key: "marketplace",
      label: "Marketplace",
      render: (row) => (
        <span className="order-dashboard-marketplace">
          {formatMarketplace(row.marketplace)}
        </span>
      ),
    },
    { key: "total_orders", label: "Total Orders", render: (row) => toNumber(row.total_orders) },
    { key: "new_orders", label: "Pending at Seller", render: (row) => toNumber(row.new_orders) },
    {
      key: "in_progress_orders",
      label: "In Progress",
      render: (row) => toNumber(row.in_progress_orders),
    },
    {
      key: "cancelled_orders",
      label: "Cancelled",
      render: (row) => toNumber(row.cancelled_orders),
    },
  ];

  const platformColumns = [
    {
      key: "marketplace",
      label: "Marketplace",
      render: (row) => (
        <span className="order-dashboard-marketplace">
          {formatMarketplace(row.marketplace)}
        </span>
      ),
    },
    { key: "total_orders", label: "Total Orders", render: (row) => toNumber(row.total_orders) },
    { key: "new_orders", label: "Pending at Seller", render: (row) => toNumber(row.new_orders) },
    {
      key: "in_progress_orders",
      label: "In Progress",
      render: (row) => toNumber(row.in_progress_orders),
    },
    {
      key: "cancelled_orders",
      label: "Cancelled",
      render: (row) => toNumber(row.cancelled_orders),
    },
  ];

  const vendorColumns = [
    {
      key: "vendor_name",
      label: "Vendor Name",
      render: (row) => row.vendor_name || "—",
    },
    {
      key: "marketplaces",
      label: "Marketplace",
      render: (row) => {
        const value = row.marketplaces || row.marketplace || "";
        if (!value) return "—";
        return String(value)
          .split(",")
          .map((m) => formatMarketplace(m.trim()))
          .join(", ");
      },
    },
    { key: "total_orders", label: "Total Orders", render: (row) => toNumber(row.total_orders) },
    { key: "new_orders", label: "Pending at Seller", render: (row) => toNumber(row.new_orders) },
    {
      key: "in_progress_orders",
      label: "In Progress",
      render: (row) => toNumber(row.in_progress_orders),
    },
    {
      key: "cancelled_orders",
      label: "Cancelled",
      render: (row) => toNumber(row.cancelled_orders),
    },
  ];

  const roleHint =
    userRole === "seller"
      ? "Showing your order metrics only."
      : "Showing all company order metrics.";

  return (
    <PageWrapper
      title="Order Dashboard"
      description="Track order volume, status breakdowns, and performance by client, platform, and vendor."
    >
      <div className="order-dashboard-page">
        <div className="order-dashboard-filters">
          <label className="order-dashboard-date-label">
            From
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, from: e.target.value }))
              }
            />
          </label>
          <label className="order-dashboard-date-label">
            To
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, to: e.target.value }))
              }
            />
          </label>
          <MDBBtn color="primary" onClick={applyDateFilter} disabled={loading}>
            {loading ? "Loading..." : "Apply Filter"}
          </MDBBtn>
          <span className="order-dashboard-filter-hint">
            {roleHint}
            {appliedDateRange.from && appliedDateRange.to
              ? ` · ${appliedDateRange.from} to ${appliedDateRange.to}`
              : ""}
          </span>
        </div>

        {error && <div className="order-dashboard-error">{error}</div>}

        {loading && !stats ? (
          <div className="order-dashboard-loading">
            <MDBSpinner role="status" />
            <span>Loading dashboard...</span>
          </div>
        ) : (
          <>
            <div className="order-dashboard-stats">
              <StatCard
                label="Total Orders"
                value={overall.total_orders}
                variant="highlight"
              />
              {marketplaceStats.map((item) => (
                <StatCard
                  key={item.marketplace}
                  label={`Orders on ${item.label}`}
                  value={item.total_orders}
                  variant="marketplace"
                />
              ))}
              <StatCard label="Pending at Seller" value={overall.new_orders} />
              <StatCard label="Dispatched" value={overall.dispatched_orders} />
              <StatCard label="Delivered" value={overall.delivered_orders} />
              <StatCard label="In Progress" value={overall.in_progress_orders} />
              <StatCard label="Cancelled" value={overall.cancelled_orders} />
            </div>

          {userRole !== "seller" && (
            <div>
     <DashboardTable
              title="Client-wise View"
              columns={clientColumns}
              rows={clientWise.map((row) => ({
                ...row,
                id: `${row.marketplace}-${row.store_id}`,
              }))}
              emptyMessage="No client store data for the selected date range."
            />
            </div>
       
          )}

            <DashboardTable
              title="Platform-wise View"
              columns={platformColumns}
              rows={platformWise.map((row) => ({
                ...row,
                id: row.marketplace,
              }))}
              emptyMessage="No platform data for the selected date range."
            />

            <DashboardTable
              title="Vendor-wise View"
              columns={vendorColumns}
              rows={vendorWise.map((row) => ({
                ...row,
                id: row.vendor_id,
              }))}
              emptyMessage="No vendor data for the selected date range."
            />
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default OrderDashboardPage;
